import { useTranslation } from "react-i18next";
import { CONTACTS } from "src/config/contacts";
import { genericWhatsappHref } from "src/utils/whatsapp";
import { Instagram } from "../../assets/icons/icons";
import { Telegram, Whatsapp, Youtube } from "../../assets/icons/social-media";
import { SITE_IMAGES } from "src/config/images";
import { Languages } from "../language-switcher";
import Logo from "../logo";

// ПОДВАЛ САЙТА.
//
// Раньше здесь были только переключатель языков и четыре иконки соцсетей —
// человек, долиставший страницу до конца, не находил ни телефона, ни почты,
// ни ссылок на разделы. Теперь это нормальный подвал: знак и короткое
// описание, навигация, живые контакты и языки, а снизу — копирайт.
//
// Пустые значения в src/config/contacts.js сами убирают свою строку:
// заполнять всё подряд не нужно, лишних пунктов не появится.
const socials = [
  { name: "WhatsApp", href: CONTACTS.whatsapp, icon: Whatsapp },
  { name: "Telegram", href: CONTACTS.telegram, icon: Telegram },
  { name: "Instagram", href: CONTACTS.instagram, icon: Instagram },
  { name: "YouTube", href: CONTACTS.youtube, icon: Youtube },
].filter((item) => item.href);

// Фон подвала — меняется в src/config/images.js (ключ bottom).
const footerPhotoStyle = {
  backgroundImage: `url(${SITE_IMAGES.bottom})`,
  backgroundSize: "cover",
  backgroundPosition: "center 35%",
};

const ColumnTitle = ({ children }) => (
  <h2 className="text-xs font-extrabold uppercase tracking-[0.2em] text-kj-gold">
    {children}
  </h2>
);

const Footer = () => {
  const { t } = useTranslation();

  const navLinks = [
    { href: "/activities", label: t("header.activities") },
    { href: "/services", label: t("header.services") },
    { href: "/about", label: t("header.about") },
    { href: "/contacts", label: t("header.contactUs") },
    { href: "/contacts#faq", label: t("header.faq") },
  ];

  // Телефон и почта — ссылками: на телефоне номер набирается в одно касание,
  // а не выделяется и копируется вручную.
  const contactLines = [
    CONTACTS.phone && { href: `tel:${CONTACTS.phone.replace(/[^\d+]/g, "")}`, label: CONTACTS.phone },
    CONTACTS.email && { href: `mailto:${CONTACTS.email}`, label: CONTACTS.email },
  ].filter(Boolean);

  return (
    <footer id="contacts" className="w-full relative z-10 bg-kj-dark">
      <div
        className="absolute inset-0 photo-fade-top"
        style={footerPhotoStyle}
        aria-hidden="true"
      />
      {/* Затемнение: плотное сверху (стык со страницей) и снизу (там копирайт
          и нижняя строка), приоткрытое в середине — фотография должна
          читаться как фактура, но не спорить с текстом колонок. */}
      <div className="absolute inset-0 bg-gradient-to-b from-kj-dark via-kj-dark/65 to-kj-dark/90" aria-hidden="true" />

      <div className="relative z-10 max-w-[1526px] w-full mx-auto px-4 pt-12 sm:pt-16 pb-8">
        <div className="grid gap-10 sm:gap-12 md:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))]">
          {/* Знак и одна строка о компании */}
          <div>
            <Logo />
            <p className="mt-5 max-w-[36ch] text-sm text-kj-sub leading-relaxed">
              {t("footer.tagline")}
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex items-center gap-x-4">
                {socials.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-kj-ink transition-colors hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark rounded-sm"
                  >
                    <span className="sr-only">{item.name}</span>
                    <item.icon className="h-6 w-6" aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Разделы сайта */}
          <nav aria-labelledby="footer-nav">
            <ColumnTitle>
              <span id="footer-nav">{t("footer.navTitle")}</span>
            </ColumnTitle>
            <ul className="mt-4 flex flex-col gap-y-2.5">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="text-sm text-kj-sub transition-colors hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold rounded-sm"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Живые контакты */}
          <div>
            <ColumnTitle>{t("footer.contactUsText")}</ColumnTitle>
            <ul className="mt-4 flex flex-col gap-y-2.5">
              {contactLines.map((line) => (
                <li key={line.href}>
                  <a
                    href={line.href}
                    className="text-sm text-kj-sub transition-colors hover:text-kj-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold rounded-sm break-all"
                  >
                    {line.label}
                  </a>
                </li>
              ))}
              {CONTACTS.whatsapp && (
                <li>
                  <a
                    href={genericWhatsappHref(t)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center rounded-sm border border-kj-gold px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-kj-gold transition-colors hover:bg-kj-gold hover:text-kj-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kj-gold focus-visible:ring-offset-2 focus-visible:ring-offset-kj-dark"
                  >
                    {t("footer.writeWhatsapp")}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Языки */}
          <div>
            <ColumnTitle>{t("header.languageSwitcher")}</ColumnTitle>
            <Languages className="mt-3 !justify-start -ml-3" />
          </div>
        </div>

        <div className="mt-10 border-t border-kj-line pt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-kj-sub">© {t("footer.copyrightText")}</p>
          <p className="text-xs text-kj-sub">{t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
